import { BlockTitle } from "@blocks/Blocks";
import SplideArrows from "@components/Slider/components/SplideArrows";
import { MyBTObject } from "@features/mybt/utlis/types";
import {
  Options,
  Splide,
  SplideSlide,
  SplideTrack,
} from "@greglaisph/react-splide";
import { TestIdClassNameProps } from "@typings/Common";
import { cn } from "@util/helpers";

import MyBTCarouselSlide from "./MyBTCarouselSlide";

type MyBTCarouselProps = TestIdClassNameProps & {
  /** The results of the carousel. */
  myBTObjects: MyBTObject[];
  /** The title of the carousel. */
  title?: string;
  /** The class name for the wrapper of the arrows */
  arrowWrapperClassName?: string;
  /** The class name for the wrapper of the slide */
  slideWrapperClassName?: string;
  /** The class name for the divider */
  dividerClassName?: string;
  /** The class name for the icon */
  iconClassName?: string;
  /** The class name for the block title */
  className?: string;
  /** The ref code to be added on the carousel. */
  refCode?: string;
  /** The header of the carousel. */
  header?: React.ReactNode;
};

/**
 * MyBTCarousel is a component that displays a carousel of MyBT objects.
 */
const MyBTCarousel = ({
  title,
  testId,
  className,
  myBTObjects,
  arrowWrapperClassName,
  slideWrapperClassName,
  dividerClassName,
  iconClassName,
  refCode,
  header,
}: MyBTCarouselProps): React.ReactElement => {
  const options: Options = {
    pagination: false,
    type: "slide",
    autoplay: false,
    interval: 10000,
    perMove: 1,
    gap: "1.5rem",
    focus: 0,
    omitEnd: true,
    mediaQuery: "min",
    arrows: false,
    fixedWidth: "65%",
    breakpoints: {
      768: { fixedWidth: "38%", arrows: true },
      992: { perPage: 4, fixedWidth: undefined },
    },
  };

  return (
    <div className={className} data-testid={testId}>
      {header ? (
        header
      ) : (
        <BlockTitle
          text={title || ""}
          rootClassName="mt-5 text-lg font-bold uppercase text-gray-850 lg:text-3xl font-lucida"
        />
      )}

      <Splide options={options} tag="div" hasTrack={false}>
        <SplideArrows
          className={cn(
            "absolute -top-9 right-0 flex flex-row items-center justify-center gap-2",
            arrowWrapperClassName
          )}
        />

        <SplideTrack>
          {myBTObjects.map((object) => {
            return (
              <SplideSlide key={object.tag.id} className="pt-6">
                <MyBTCarouselSlide
                  myBTObject={object}
                  className={slideWrapperClassName}
                  dividerClassName={dividerClassName}
                  iconClassName={iconClassName}
                  refCode={refCode}
                />
              </SplideSlide>
            );
          })}
        </SplideTrack>
      </Splide>
    </div>
  );
};

export default MyBTCarousel;
